export type TeamCoverage = 'none' | 'partial' | 'all';
export type TeamIntent = 'unchanged' | 'add' | 'remove';

export type EditTeamOption = {
  id: string;
  name: string;
  color?: string;
  count: number;
  coverage: TeamCoverage;
  canUpdate: boolean;
};

export const nextTeamIntent = (
  coverage: TeamCoverage,
  current: TeamIntent
): TeamIntent => {
  if (coverage === 'none') {
    return current === 'add' ? 'unchanged' : 'add';
  }
  if (coverage === 'all') {
    return current === 'remove' ? 'unchanged' : 'remove';
  }
  if (current === 'unchanged') return 'add';
  if (current === 'add') return 'remove';
  return 'unchanged';
};

export const getTeamCheckboxState = (
  coverage: TeamCoverage,
  intent: TeamIntent
): { checked: boolean; indeterminate: boolean } => {
  if (intent === 'add') return { checked: true, indeterminate: false };
  if (intent === 'remove') return { checked: false, indeterminate: false };
  if (coverage === 'all') return { checked: true, indeterminate: false };
  if (coverage === 'partial') return { checked: false, indeterminate: true };
  return { checked: false, indeterminate: false };
};

export const getTeamStatusLabel = (
  coverage: TeamCoverage,
  intent: TeamIntent,
  count: number,
  total: number
): { text: string; tone: 'muted' | 'add' | 'remove' } | null => {
  if (intent === 'add') return { text: 'Will add to all', tone: 'add' };
  if (intent === 'remove')
    return { text: 'Will remove from all', tone: 'remove' };
  if (coverage === 'none') return null;
  return { text: `On ${count} of ${total}`, tone: 'muted' };
};

export const coverageFromCount = (
  count: number,
  total: number
): TeamCoverage => {
  if (total <= 0 || count <= 0) return 'none';
  if (count >= total) return 'all';
  return 'partial';
};

/** Keep current assignments first; pending adds append at the bottom. */
export const sortOnSelectedTeams = <T extends { id: string }>(
  teams: T[],
  intents: Record<string, TeamIntent>
): T[] => {
  const existing: T[] = [];
  const pendingAdds: T[] = [];
  for (const team of teams) {
    if ((intents[team.id] ?? 'unchanged') === 'add') {
      pendingAdds.push(team);
    } else {
      existing.push(team);
    }
  }
  return [...existing, ...pendingAdds];
};
